import {
  Badge,
  Box,
  Button,
  Flex,
  type RuleSearchResult,
  RuleSearchResults,
  Text,
  Textarea,
  Tooltip,
} from '@rocket/ui';
import { useEffect, useRef, useState } from 'react';
import { LuCheck, LuGitBranch, LuMapPin, LuSend, LuShield } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { ChatRuleCreated, ChatRuleSearchResult, ChatSessionContext } from '../onboarding-api';
import { copyRuleToPlaybook, onboardingChat } from '../onboarding-api';
import { RuleExampleCarousel } from './RuleExampleCarousel';

/** Convert ChatRuleSearchResult to RuleSearchResult for RuleSearchCard component */
function convertToRuleSearchResult(chatResult: ChatRuleSearchResult): RuleSearchResult {
  return {
    rule_id: chatResult.ruleId,
    name: chatResult.name,
    description: chatResult.description,
    playbook_id: chatResult.playbookId,
    playbook_name: chatResult.playbookName,
    training_count: chatResult.trainingCount,
    confidence: chatResult.confidence,
    sample_edits: chatResult.sampleEdits || [],
    train_types: [],
    sample_sentences: [],
  };
}

/** Renders basic markdown bold (**text**) as <strong> in chat messages. */
function ChatMessageText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <Text key={i} as="span" fontWeight="bold">
              {part.slice(2, -2)}
            </Text>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  rulesCreated?: Array<ChatRuleCreated>;
  searchResults?: Array<ChatRuleSearchResult>;
}

const RULE_FAMILY_LABELS: Record<string, string> = {
  point_edit: 'Point edit',
  fsi: 'Sentence insert',
  fpi: 'Paragraph insert',
  fsd: 'Sentence delete',
  fpd: 'Paragraph delete',
  list_edit: 'List edit',
  comment: 'Comment',
};

function RuleCreatedCard({ rule }: { rule: ChatRuleCreated }) {
  const familyLabel = RULE_FAMILY_LABELS[rule.ruleFamily ?? ''] ?? rule.ruleFamily ?? 'Rule';
  const isTail = rule.alignmentMode === 'TAIL' && !!rule.tailSectionName;

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="green.muted"
      bg="green.subtle"
      px="3"
      py="2"
      w="100%"
    >
      <Flex.H gap="1" align="center" mb={isTail ? '1' : '0'}>
        <LuCheck size={12} color="var(--chakra-colors-green-fg)" />
        <Text fontSize="xs" fontWeight="600" color="green.fg">
          {rule.name}
        </Text>
        <Badge size="sm" variant="outline" colorPalette="green" ml="1">
          {familyLabel}
        </Badge>
      </Flex.H>
      {isTail && (
        <Flex.H gap="1" align="center" mt="1">
          <LuMapPin size={10} color="var(--chakra-colors-fg-muted)" />
          <Text fontSize="xs" color="fg.muted">
            Inserts after <strong>{rule.tailSectionName}</strong>
          </Text>
          {rule.contractType && (
            <Text fontSize="xs" color="fg.subtle" ml="1">
              (using {rule.contractType} sections)
            </Text>
          )}
        </Flex.H>
      )}
    </Box>
  );
}

const FIRST_PARTY_PROMPTS = [
  {
    label: 'Protect key language',
    example: 'Protect my limitation of liability clause — keep the cap at 2x fees',
  },
  {
    label: 'Add fallback language',
    example: 'Add a fallback for the indemnification clause if the counterparty pushes back',
  },
  {
    label: 'Lock preferred terms',
    example: 'Lock the governing law to New York and the forum to Southern District of NY',
  },
];

interface Props {
  playbookId: string | undefined;
  contractType: string;
  onRulesCreated?: (rules: Array<ChatRuleCreated>) => void;
  /** When true the component fills its parent height (flex column) */
  fillHeight?: boolean;
}

/**
 * Chat box specialized for first-party paper playbook building.
 * Includes "Protect" and "Add Fallbacks" quick-action buttons that read
 * the currently selected text from the open Word document.
 */
export function FirstPartyPaperChatBox({
  playbookId,
  contractType,
  onRulesCreated,
  fillHeight,
}: Props) {
  const { bbAxios, getSelectionText } = useFtueHost();
  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionContext, setSessionContext] = useState<ChatSessionContext>({});
  const [chatHistory, setChatHistory] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [_addingRuleIds, setAddingRuleIds] = useState<Set<string>>(new Set());
  const [addedRuleIds, setAddedRuleIds] = useState<Set<string>>(new Set());
  const [dismissedCarouselIds, setDismissedCarouselIds] = useState<Set<string>>(new Set());
  const [_dismissedSearchCarouselIds, _setDismissedSearchCarouselIds] = useState<Set<string>>(
    new Set()
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  /**
   * Read the current live-document selection and populate the input with a prefixed
   * prompt. Capability-branch seam: hosts with a live selection (add-in Word) provide
   * getSelectionText; where it's absent (web upload flow) the quick buttons that call
   * this are hidden, so this just prefills the prompt with no selected text.
   */
  const prefillFromSelection = async (prefix: string) => {
    try {
      const selectedText = (await getSelectionText?.())?.trim() ?? '';
      if (selectedText) {
        setInput(`${prefix}: "${selectedText}"`);
      } else {
        setInput(`${prefix}: `);
      }
      textareaRef.current?.focus();
    } catch {
      setInput(`${prefix}: `);
      textareaRef.current?.focus();
    }
  };

  const handleProtect = () => void prefillFromSelection('Protect this language');
  const handleAddFallbacks = () => void prefillFromSelection('Add fallback options for');

  const handleAddRule = async (result: ChatRuleSearchResult) => {
    if (!playbookId || addedRuleIds.has(result.ruleId)) {
      return;
    }
    setAddingRuleIds((prev) => new Set(prev).add(result.ruleId));
    try {
      await copyRuleToPlaybook(bbAxios, { sourceRuleId: result.ruleId, playbookId });
      setAddedRuleIds((prev) => new Set(prev).add(result.ruleId));
      onRulesCreated?.([{ ruleId: result.ruleId, name: result.name }]);
    } catch {
      // silently ignore — user can retry
    } finally {
      setAddingRuleIds((prev) => {
        const next = new Set(prev);
        next.delete(result.ruleId);
        return next;
      });
    }
  };

  const sendMessage = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || isSending || !playbookId) {
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);

    try {
      const result = await onboardingChat(bbAxios, {
        playbookId,
        contractType,
        message: text,
        sessionContext,
        chatHistory,
      });

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        rulesCreated: result.rulesCreated.length > 0 ? result.rulesCreated : undefined,
        searchResults: result.searchResults?.length > 0 ? result.searchResults : undefined,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setSessionContext(result.sessionContext);
      setChatHistory(result.chatHistory ?? []);

      if (result.rulesCreated.length > 0) {
        onRulesCreated?.(result.rulesCreated);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="border"
      p="3"
      display="flex"
      flexDirection="column"
      flex={fillHeight ? '1' : undefined}
      minH={fillHeight ? '0' : '300px'}
      w="100%"
    >
      <Flex.H align="center" justify="space-between" mb="3">
        <Text fontSize="sm" fontWeight="semibold">
          First Party Paper Assistant
        </Text>
        {/* Quick-action buttons that read the live document selection — only shown
            where the host exposes a live selection (add-in Word). Hidden on web. */}
        {getSelectionText && (
          <Flex.H gap="1">
            <Tooltip
              trigger={
                <Button
                  size="xs"
                  variant="outline"
                  colorPalette="blue"
                  onClick={handleProtect}
                  disabled={!playbookId}
                >
                  <LuShield size={12} />
                  Protect
                </Button>
              }
            >
              Protect the text selected in Word — creates a rule to preserve your preferred language
            </Tooltip>
            <Tooltip
              trigger={
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleAddFallbacks}
                  disabled={!playbookId}
                >
                  <LuGitBranch size={12} />
                  Add Fallbacks
                </Button>
              }
            >
              Add fallback alternatives for the text selected in Word
            </Tooltip>
          </Flex.H>
        )}
      </Flex.H>

      {/* Message history / empty state */}
      <Box ref={scrollRef} flex="1" minH="0" w="100%" overflowY="auto" mb="3">
        {isEmpty ? (
          <Flex.V gap="2" w="100%">
            <Text fontSize="xs" color="fg.muted">
              Select text in your document and click <strong>Protect</strong> or{' '}
              <strong>Add Fallbacks</strong>, or try one of these:
            </Text>
            {FIRST_PARTY_PROMPTS.map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                h="auto"
                py="2"
                onClick={() => setInput(p.example)}
                disabled={!playbookId}
              >
                <Flex.V gap="0" alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="semibold">
                    {p.label}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="normal">
                    e.g. "{p.example}"
                  </Text>
                </Flex.V>
              </Button>
            ))}
            {!playbookId && (
              <Text fontSize="xs" color="fg.subtle" mt="1">
                Save your playbook first to use the assistant.
              </Text>
            )}
          </Flex.V>
        ) : (
          <Box display="flex" flexDirection="column" gap="2" w="100%">
            {messages.map((msg, i) => (
              <Box key={i} w="100%">
                <Box
                  bg={msg.role === 'user' ? 'blue.subtle' : 'bg.surface'}
                  borderRadius="md"
                  px="3"
                  py="2"
                  maxW="90%"
                  css={{ marginLeft: msg.role === 'user' ? 'auto' : undefined }}
                >
                  <Text fontSize="xs" whiteSpace="pre-wrap">
                    <ChatMessageText text={msg.content} />
                  </Text>
                </Box>

                {/* Rule-created cards */}
                {msg.rulesCreated && msg.rulesCreated.length > 0 && (
                  <Box mt="1" display="flex" flexDirection="column" gap="1">
                    {msg.rulesCreated.map((r) => (
                      <Box key={r.ruleId}>
                        <RuleCreatedCard rule={r} />
                        {!dismissedCarouselIds.has(r.ruleId) && (
                          <RuleExampleCarousel
                            ruleId={r.ruleId}
                            onDismiss={() =>
                              setDismissedCarouselIds((prev) => new Set(prev).add(r.ruleId))
                            }
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Search result cards - now using RuleSearchCard with inline sample edits */}
                {msg.searchResults && msg.searchResults.length > 0 && (
                  <Box mt="2">
                    {/* Show section fallback message if applicable */}
                    {msg.searchResults.some((r) => r.sectionFallback) && (
                      <Box mb="2" p="2" bg="blue.subtle" borderRadius="sm">
                        <Text fontSize="xs" color="blue.fg">
                          No exact matches found. Here are rules from the{' '}
                          <strong>
                            {msg.searchResults.find((r) => r.sectionFallback)?.sectionFallback}
                          </strong>{' '}
                          section:
                        </Text>
                      </Box>
                    )}
                    <RuleSearchResults
                      results={msg.searchResults.map(convertToRuleSearchResult)}
                      onSelect={(rule) => {
                        // Convert back to ChatRuleSearchResult to call handleAddRule
                        const chatResult = msg.searchResults?.find(
                          (r) => r.ruleId === rule.rule_id
                        );
                        if (chatResult) {
                          void handleAddRule(chatResult);
                        }
                      }}
                      disabled={isSending}
                    />
                    {/* Show "Added" status badges after the cards */}
                    {msg.searchResults.some((r) => addedRuleIds.has(r.ruleId)) && (
                      <Box mt="2">
                        {msg.searchResults
                          .filter((r) => addedRuleIds.has(r.ruleId))
                          .map((result) => (
                            <Flex.H key={result.ruleId} align="center" gap="1" mb="1">
                              <LuCheck size={12} color="var(--colors-green-fg)" />
                              <Text fontSize="xs" color="green.fg">
                                {result.name} added to playbook
                              </Text>
                            </Flex.H>
                          ))}
                      </Box>
                    )}
                    {/* "Get more results" button if this was a search result message */}
                    {i === messages.length - 1 && msg.searchResults.length > 0 && !isSending && (
                      <Button
                        size="xs"
                        variant="outline"
                        mt="2"
                        onClick={() => void sendMessage('Show me 5 more results')}
                      >
                        Get 5 more results
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            ))}
            {isSending && (
              <Box bg="bg.surface" borderRadius="md" px="3" py="2" maxW="60%">
                <Text fontSize="xs" color="fg.muted">
                  Thinking…
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Input */}
      <Flex.H gap="2" align="flex-end">
        <Box flex="1">
          <Textarea
            ref={textareaRef}
            placeholder="Describe a rule, or select text and click Protect / Add Fallbacks…"
            value={input}
            onChange={(e) => setInput((e.target as HTMLTextAreaElement).value)}
            onKeyDown={handleKeyDown}
            disabled={isSending || !playbookId}
            rows={2}
          />
        </Box>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={() => void sendMessage()}
          disabled={!input.trim() || isSending || !playbookId}
          loading={isSending}
          aria-label="Send"
        >
          <LuSend size={14} />
        </Button>
      </Flex.H>
    </Box>
  );
}
